import { Button, Card, Grid, Group, SegmentedControl, Text, TextInput } from '@mantine/core';
import { Editor } from '@monaco-editor/react';
import { IconArrowsMaximize, IconArrowsMinimize, IconCode, IconInfoCircle, IconPhoto } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import useFetch from '../../helpers/useFetch';
import AssetList from '../Assets/AssetList';
import UnlayerEditor from '../Editors/UnlayerEditor';
import MergeTagsCollapsible from '../MergeTagsCollapsible';
import mergeTagsForEmailEditor from '../mergeTagsForEmailEditor';
import ConfirmationModal from '../Modals/ConfirmationModal';
import PreviewHTML from '../PreviewHTML';
import Spinner from '../Spinner';
import TagsSelect from '../TagSelector';

const ContentBannerForm = ({
  banner,
  handleStateChange,
  dynamicFieldsOpen,
  mergeTags,
  setDynamicFieldsOpen,
  setUserError,
  unlayerEditor,
  accountId,
  domainId,
  setLoadError,
  switchToHtml,
  unlayerLoaded,
  setUnlayerLoaded,
}) => {
  const [templates, setTemplates] = useState();
  const [productFeeds, setProductFeeds] = useState();
  const [expanded, setExpanded] = useState(false);
  const [assetModalViewOpen, setAssetModalViewOpen] = useState(false);
  const [confirmSwitchToHtmlModalOpen, setConfirmSwitchToHtmlModalOpen] = useState(false);

  const fetch = useFetch();

  let switchToHtmlButton;
  let expandCollapseButton;
  let viewAssetsButton;

  if (banner.editor === 'visual') {
    switchToHtmlButton = (
      <Button
        size="sm"
        type="button"
        disabled={banner.editor === 'visual' && !unlayerLoaded}
        onClick={() => setConfirmSwitchToHtmlModalOpen(true)}
      >
        <IconCode size={14} /> Switch to HTML
      </Button>
    );
  } else {
    if (!switchToHtmlButton) {
      if (expanded) {
        expandCollapseButton = (
          <Button size="sm" type="button" onClick={() => setExpanded(false)}>
            <IconArrowsMinimize size={14} /> Collapse Preview and Editor
          </Button>
        );
      } else {
        expandCollapseButton = (
          <Button size="sm" type="button" onClick={() => setExpanded(true)}>
            <IconArrowsMaximize size={14} /> Expand Preview and Editor
          </Button>
        );
      }

      if (assetModalViewOpen) {
        viewAssetsButton = (
          <Button size="sm" onClick={() => setAssetModalViewOpen(false)}>
            <IconPhoto size={14} /> Hide Images
          </Button>
        );
      } else {
        viewAssetsButton = (
          <Button size="sm" onClick={() => setAssetModalViewOpen(true)}>
            <IconPhoto size={14} /> View Images
          </Button>
        );
      }
    }
  }

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/templates`)
      .then(({ templates }) => setTemplates(templates.filter((t) => t.scope === 'web')))
      .catch(setLoadError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/productFeeds`)
      .then(({ productFeeds }) => setProductFeeds(productFeeds.filter((f) => f.scope === 'web')))
      .catch(setLoadError);
  }, [fetch, accountId, domainId, setLoadError]);

  if (templates === undefined || productFeeds === undefined) {
    return <Spinner />;
  }

  let unlayerProjectId = '65057';
  if (banner.scope === 'postPurchase') {
    unlayerProjectId = '177436';
  }

  const handleChangeAce = (key, value) => {
    if (key === 'html') {
      handleStateChange({ target: { name: 'html', value: value } });
    }
  };

  const templatesData = {
    editor: {
      data: {
        options: [{ label: '', value: '' }, ...templates.map((t) => ({ label: t.name, value: t.id.toString() }))],
      },
    },
  };

  const productFeedsData = {
    editor: {
      data: {
        options: [{ label: '', value: '' }, ...productFeeds.map((p) => ({ label: p.name, value: p.id.toString() }))],
      },
    },
  };

  const tools =
    banner.scope === 'banner'
      ? {
          form: {
            properties: {
              action: {
                editor: {
                  data: {
                    allowCustomUrl: true,
                  },
                },
              },
            },
          },
        }
      : {
          'custom#my_post_purchase': {
            properties: {
              template: templatesData,
              recommendersTemplate: templatesData,
              '1RecommenderProductFeed': productFeedsData,
              '2RecommenderProductFeed': productFeedsData,
              '3RecommenderProductFeed': productFeedsData,
            },
          },
        };

  const editor =
    banner.editor === 'visual' ? (
      <Grid mb="md">
        <Grid.Col span={{ base: 12 }}>
          <UnlayerEditor
            editorHeight="100vh"
            design={banner.design}
            editorRef={unlayerEditor}
            displayMode="web"
            onLoad={() => setUnlayerLoaded(true)}
            projectId={unlayerProjectId}
            mergeTags={mergeTagsForEmailEditor(mergeTags)}
            tools={tools}
          />
        </Grid.Col>
      </Grid>
    ) : (
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: assetModalViewOpen ? 8 : 6 }} className={expanded ? 'FullWidth' : ''}>
          <Grid>
            <Grid.Col
              span={{ base: 12, sm: assetModalViewOpen ? 6 : 12 }}
              display={assetModalViewOpen ? 'block' : 'none'}
            >
              <div className={expanded ? 'assetsPreviewBox overflow-auto' : 'assetsPreviewBox overflow-auto'}>
                <AssetList />
              </div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: assetModalViewOpen ? 6 : 12 }}>
              <PreviewHTML htmlPreview={banner.html} />
            </Grid.Col>
          </Grid>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: assetModalViewOpen ? 4 : 6 }} className={expanded ? 'FullWidth' : ''}>
          <Editor
            defaultLanguage="html"
            style={{ width: '100%' }}
            onChange={(value) => handleChangeAce('html', value)}
            name="html"
            height="330pt"
            value={banner.html}
          />
        </Grid.Col>
      </Grid>
    );
  return (
    <Card data-tour="bannerFormVisualContent" withBorder>
      <Card.Section inheritPadding py="sm">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Name
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="name"
              type="text"
              value={banner.name}
              placeholder="e.g. Similar Products"
              onChange={(e) => handleStateChange(e)}
            />
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Scope*
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <SegmentedControl
              value={banner.scope ?? ''}
              disabled={!!banner.id}
              onChange={(scope) => handleStateChange({ target: { name: 'scope', value: scope } })}
              data={[
                { label: 'Standard', value: 'banner' },
                { label: 'Post-purchase', value: 'postPurchase' },
              ]}
            />
            <Text size="sm" c="dimmed">
              In <strong>Standard</strong> scope, you can drag and drop elements to build banners, including
              subscription forms.
              <br />
              In <strong>Post-purchase</strong> scope, you can create a post-purchase block to be used in an order
              status page.
            </Text>
          </Grid.Col>
        </Grid>
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Text size="sm" fw={500}>
              Tags
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TagsSelect model={banner} handleChange={handleStateChange} setUserError={setUserError} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Group justify="flex-end" gap="xs">
              {viewAssetsButton}
              {expandCollapseButton}
              {switchToHtmlButton}
              <Button
                size="sm"
                variant="default"
                onClick={() => setDynamicFieldsOpen(!dynamicFieldsOpen)}
                aria-expanded={dynamicFieldsOpen}
              >
                <IconInfoCircle size={14} /> Merge Tags
              </Button>
            </Group>
          </Grid.Col>
        </Grid>
        <MergeTagsCollapsible open={dynamicFieldsOpen} mergeTags={mergeTags} />

        <Grid mb="md">
          <Grid.Col span={{ base: 12 }}>{editor}</Grid.Col>
        </Grid>
        <ConfirmationModal
          show={confirmSwitchToHtmlModalOpen}
          close={() => setConfirmSwitchToHtmlModalOpen(false)}
          confirm={(cb) => switchToHtml(cb)}
          message="Once you switch to HTML mode, you cannot switch back to using the visual editor."
        />
      </Card.Section>
    </Card>
  );
};
export default ContentBannerForm;
